import { Injectable, OnDestroy } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UserColorSchemeService } from '@y42/browser/user-color-scheme';
import {
  fromModelChanged,
  GojsContextMenuService,
  GoJsSearchNode,
  highlightNodes,
  IS_SIDEBAR_TYPE_PROP,
} from '@y42/gojs';
import { DiagramEvent, Iterator, Node, Part } from 'gojs';
import isEqual from 'lodash/isEqual';
import {
  BehaviorSubject,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  map,
  share,
  Subject,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { OrchestrationDiagramParts } from '../gojs/config/orchestration-part-names';
import { OrchestrationDiagram } from '../gojs/orchestration-diagram';
import { addLinksToDiagram, addNodesAndLinksToDiagram, addNodesToDiagram } from '../gojs/utils/diagram-utils';
import {
  OrchestrationDiagramConfig,
  OrchestrationDiagramUIModel,
  OrchestrationUINode,
  OrchestrationUndoManagerStats,
} from '../models/orchestration-diagram.model';
import { OrchestrationDiagramDataService } from './orchestration-diagram-data.service';
import { OrchestrationDiagramHydrationService } from './orchestration-diagram-hydration.service';

@UntilDestroy()
@Injectable()
export class OrchestrationDiagramService implements OnDestroy {
  constructor(
    private readonly orchestrationDiagramDataService: OrchestrationDiagramDataService,
    private readonly orchestrationDiagramHydrationService: OrchestrationDiagramHydrationService,
    private readonly gojsContextMenuService: GojsContextMenuService,
    private readonly userColorSchemeService: UserColorSchemeService,
  ) {
    this.diagramConfigListener();
    this.diagramOutputModelListener();
    this.addDiagramEventListeners();
    this.activeNodeListener();
    this.undoManagerStatsListener();
    this.calculateSearchNodes();
  }

  private readonly _diagram = OrchestrationDiagram({ gojsContextMenuService: this.gojsContextMenuService });
  private readonly _diagramConfig$$ = new BehaviorSubject<OrchestrationDiagramConfig | undefined>(undefined);
  private readonly _loading$$ = new BehaviorSubject(false);
  private readonly _outputModel$$ = new Subject<OrchestrationDiagramUIModel>();
  private readonly _activeNode$$ = new BehaviorSubject<OrchestrationUINode | undefined>(undefined);
  private readonly _undoMangerStats$$ = new BehaviorSubject<OrchestrationUndoManagerStats>({
    undoCount: 0,
    redoCount: 0,
    canRedo: false,
    canUndo: false,
  });
  private readonly _searchNodes$$ = new BehaviorSubject<GoJsSearchNode[] | undefined>(undefined);
  private readonly _redirectToAsset$$ = new BehaviorSubject<string | undefined>(undefined);

  private readonly _isEditable$ = this._diagramConfig$$.pipe(map((config) => config?.mode === 'edit'));
  private readonly _modelChanged$ = fromModelChanged(this.diagram).pipe(share());

  readonly loading$ = this._loading$$.pipe(untilDestroyed(this));
  readonly outputModel$ = this._outputModel$$.pipe(untilDestroyed(this));
  readonly isEditable$ = this._isEditable$.pipe(untilDestroyed(this));
  readonly undoMangerStats$ = this._undoMangerStats$$.pipe(untilDestroyed(this));
  readonly activeNode$ = this._activeNode$$.pipe(untilDestroyed(this));
  readonly searchNodes$ = this._searchNodes$$.pipe(untilDestroyed(this));
  readonly redirectToAsset$ = this._redirectToAsset$$.pipe(untilDestroyed(this));

  get diagram() {
    return this._diagram;
  }

  set diagramConfig(config: OrchestrationDiagramConfig) {
    this._diagramConfig$$.next(config);
  }

  ngOnDestroy() {
    this.removeDiagramEventListeners();
  }

  async addDiagramNodes(selectedItemPaths: string[], withDependencies = false): Promise<void> {
    const diagramModel = await this.orchestrationDiagramDataService.generateDiagramNodesAndLinks(
      selectedItemPaths,
      this.diagram,
      withDependencies,
    );
    const nodes = diagramModel.nodeDataArray;
    const links = diagramModel.linkDataArray;

    addNodesAndLinksToDiagram(this.diagram, nodes, links);
    highlightNodes(
      nodes.map((n) => n.key),
      this.diagram,
    );
    if (nodes.length > 0 && this.diagram) {
      const foundNode = this.diagram.findNodeForKey(nodes[0].key);
      if (foundNode) {
        this.diagram.commandHandler.scrollToPart(foundNode);
      }
    }
  }

  addImportedModel(model: OrchestrationDiagramUIModel): void {
    const nodes = model.nodeDataArray.filter((node) => !this.diagram?.findNodeForKey(node.key));
    if (!nodes.length) {
      return;
    }

    addNodesToDiagram(this.diagram, nodes);
    const links = model.linkDataArray.filter(
      (link) =>
        this.diagram?.findLinksByExample({ to: link.to, from: link.from }).count === 0 &&
        !!this.diagram?.findNodeForKey(link.from) &&
        !!this.diagram?.findNodeForKey(link.to),
    );
    if (links.length > 0) {
      addLinksToDiagram(this.diagram, links);
    }
    this.diagram.layoutDiagram();
  }

  private addDiagramEventListeners() {
    this.diagram.addDiagramListener('ObjectSingleClicked', this.onObjectSingleClicked.bind(this));
    this.diagram.addDiagramListener('SelectionDeleted', this.onSelectionDeleted.bind(this));
  }

  private removeDiagramEventListeners() {
    this.diagram.removeDiagramListener('ObjectSingleClicked', this.onObjectSingleClicked.bind(this));
    this.diagram.removeDiagramListener('SelectionDeleted', this.onSelectionDeleted.bind(this));
  }

  // Listeners
  private diagramConfigListener() {
    this._diagramConfig$$
      .pipe(
        filter(Boolean),
        tap(() => this._loading$$.next(true)),
        switchMap((config) =>
          this.orchestrationDiagramHydrationService.orchestrationDerivedNodeDictionary$.pipe(
            take(1),
            map((dictionary) => ({ config, dictionary })),
          ),
        ),
        withLatestFrom(this.userColorSchemeService.hasDarkMode$),
        tap(([{ config, dictionary }, hasDarkMode]) =>
          this.orchestrationDiagramDataService.setDiagramModel(this.diagram, config, dictionary, hasDarkMode),
        ),
        delay(500),
        tap(() => this._loading$$.next(false)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private diagramOutputModelListener(): void {
    this._modelChanged$
      .pipe(
        debounceTime(500),
        switchMap(() => this._isEditable$),
        filter((isEditable) => isEditable),
        map(() => this.diagram.model.toJson()),
        map((modelString) => this.orchestrationDiagramDataService.parseDiagramModel(modelString)),
        distinctUntilChanged((om1, om2) => isEqual(om1, om2)),
        tap((outputModel) => this._outputModel$$.next(outputModel)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private activeNodeListener() {
    this._activeNode$$
      .pipe(
        tap((activeNode) =>
          this.orchestrationDiagramDataService.highlightActiveNodeInDiagram(this.diagram, activeNode),
        ),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private undoManagerStatsListener() {
    this._modelChanged$
      .pipe(
        debounceTime(100),
        tap(() => this.updateUndoManagerStats()),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private calculateSearchNodes() {
    this._outputModel$$
      .pipe(
        withLatestFrom(this.orchestrationDiagramHydrationService.orchestrationDerivedNodeDictionary$),
        tap(([outputModel, dictionary]) =>
          this._searchNodes$$.next(this.orchestrationDiagramDataService.generateSearchNodes(outputModel, dictionary)),
        ),
        untilDestroyed(this),
      )
      .subscribe();
  }

  // Methods
  private onObjectSingleClicked({ subject: { name, part } }: DiagramEvent): void {
    if (part?.data?.[IS_SIDEBAR_TYPE_PROP]) {
      return;
    }

    const node = part.data as OrchestrationUINode;

    const defaultHandler = () => {
      if (part instanceof Node && !node.deletedSource) {
        this.onStandardNodeClick(part);
      }
    };

    const diagramPartHandlers: Record<OrchestrationDiagramParts, () => void> = {
      [OrchestrationDiagramParts.ImportTypeButton]: () =>
        this.orchestrationDiagramDataService.toggleImportTypeOnModel(this.diagram, node),
      [OrchestrationDiagramParts.OnFailureButton]: () =>
        this.orchestrationDiagramDataService.toggleOnFailureOnModel(this.diagram, node),
      [OrchestrationDiagramParts.ShowLogsButton]: () => this.orchestrationDiagramDataService.openJobLogsForNode(node),
      [OrchestrationDiagramParts.RedirectButton]: () => this._redirectToAsset$$.next(node.path),
      [OrchestrationDiagramParts.DefaultNode]: defaultHandler,
      [OrchestrationDiagramParts.MainPanel]: defaultHandler,
      [OrchestrationDiagramParts.ContentPanel]: defaultHandler,
    };

    diagramPartHandlers[name as OrchestrationDiagramParts]?.();
  }

  private onStandardNodeClick(part: Node) {
    const node = part.data as OrchestrationUINode;
    const activeNode = this._activeNode$$.value;

    this._activeNode$$.next(node.key === activeNode?.key ? undefined : node);
  }

  private onSelectionDeleted(event: DiagramEvent): void {
    const parts = event.subject as Iterator<Part>;
    const activeNode = this._activeNode$$.value;
    parts.each((p) => {
      if (!!activeNode && activeNode?.key === p.data.key) {
        this._activeNode$$.next(undefined);
      }
    });
  }

  private updateUndoManagerStats(): void {
    const { undoManager } = this.diagram;

    const redoCount = undoManager.history.length - (undoManager.historyIndex + 1);
    const undoCount = undoManager.historyIndex + 1;

    this._undoMangerStats$$.next({
      undoCount,
      redoCount,
      canRedo: undoManager.canRedo(),
      canUndo: undoManager.canUndo(),
    });
  }
}
